import React from 'react';

import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Skeleton,
    Autocomplete,
    TextField,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useGetCaseCategories } from '../../api/categories';
import {
    getSegments,
    getTypes,
    getCategories,
    getSubcategories,
    findSubcategoryConflict,
    resolveInitialSegment,
    segmentLabel,
} from '../../utils/categoryOptions';
import styles from './CategoryEditDialog.module.css';

// Category Edit Dialog Component
//
// Cascade: ledger segment → type → category → subcategory. The segment
// (PERSONAL / BUSINESS / INVESTMENT chart, see
// .claude/references/categorization-segments/README.md) is only shown when the
// case has more than one chart; otherwise it is fixed and the dialog looks as it
// always has. It defaults to the transaction's current subcategory segment, else
// the account's ledger segment, else PERSONAL.
const CategoryEditDialog = ({ open, onClose, currentSegment, accountSegment, currentCategoryType, currentCategory, currentSubcategory, currentRationale, onSave }) => {
    const categoryTypes = "transaction";

    const { data: categoryList, isLoading: isCategoryLoading } = useGetCaseCategories(true, categoryTypes);

    const segments = getSegments(categoryList);
    const showSegment = segments.length > 1;
    const initialSegment = resolveInitialSegment(currentSegment, accountSegment, segments);

    // State to track if fields have changed from original values
    const [hasFieldsChanged, setHasFieldsChanged] = React.useState(false);
    const [rationaleCleared, setRationaleCleared] = React.useState(false);
    const [formInitialized, setFormInitialized] = React.useState(false);

    const { control, handleSubmit, reset, setValue, watch } = useForm({
        defaultValues: {
            segment: initialSegment,
            type: currentCategoryType || '',
            category: currentCategory || '',
            subcategory: currentSubcategory || '',
            rationale: currentRationale || '',
        }
    });

    // Watch form values for dependent dropdowns
    const watchedSegment = watch('segment') || initialSegment;
    const watchedType = watch('type');
    const watchedCategory = watch('category');
    const watchedSubcategory = watch('subcategory');

    // Reset form when dialog opens
    React.useEffect(() => {
        if (open) {
            reset({
                segment: initialSegment,
                type: currentCategoryType || '',
                category: currentCategory || '',
                subcategory: currentSubcategory || '',
                rationale: currentRationale || '',
            });
            setHasFieldsChanged(false);
            setRationaleCleared(false);
            setFormInitialized(false);

            // Use a small delay to ensure form is fully reset before marking as initialized
            setTimeout(() => {
                setFormInitialized(true);
            }, 0);
        }
    }, [open, initialSegment, currentCategoryType, currentCategory, currentSubcategory, currentRationale, reset]);

    // Check if any field has changed from original values and clear rationale if needed
    React.useEffect(() => {
        // Don't run this logic until the form has been properly initialized
        if (!formInitialized) {
            return;
        }

        const segmentChanged = watchedSegment !== initialSegment;
        const typeChanged = watchedType !== (currentCategoryType || '');
        const categoryChanged = watchedCategory !== (currentCategory || '');
        const subcategoryChanged = watchedSubcategory !== (currentSubcategory || '');

        const fieldsHaveChanged = segmentChanged || typeChanged || categoryChanged || subcategoryChanged;

        if (fieldsHaveChanged && !hasFieldsChanged && !rationaleCleared) {
            // First time fields changed - clear rationale
            setValue('rationale', '');
            setRationaleCleared(true);
        }

        setHasFieldsChanged(fieldsHaveChanged);
    }, [watchedSegment, watchedType, watchedCategory, watchedSubcategory, initialSegment, currentCategoryType, currentCategory, currentSubcategory, hasFieldsChanged, rationaleCleared, setValue, formInitialized]);

    const validateSubcategory = (value) => {
        const conflict = findSubcategoryConflict(categoryList, {
            segment: watchedSegment,
            type: watchedType,
            category: watchedCategory,
            subcategory: value,
        });
        if (conflict) {
            return `This subcategory already exists under "${conflict.category}" (${conflict.type}). Please choose a different name.`;
        }
        return true;
    };

    const onSubmit = (data) => {
        const { type, category, subcategory, rationale } = data;

        if (subcategory && subcategory.trim()) {
            onSave({
                segment: data.segment || initialSegment,
                type: type,
                category: category,
                subcategory: subcategory.trim(),
                rationale: rationale?.trim() || ''
            });
        }
        onClose();
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleSegmentChange = (newSegment, field) => {
        field.onChange(newSegment || initialSegment);
        // A different chart: the rest of the cascade no longer applies
        setValue('type', '');
        setValue('category', '');
        setValue('subcategory', '');
    };

    const handleCategoryTypeChange = (newType, field) => {
        field.onChange(newType || '');
        // Reset dependent fields when type changes
        setValue('category', '');
        setValue('subcategory', '');
    };

    const handleCategoryNameChange = (newCategory, field) => {
        field.onChange(newCategory || '');
        // Reset subcategory when category changes
        setValue('subcategory', '');
    };

    if (isCategoryLoading) {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Subcategory</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Box sx={{ mb: 2 }}>
                        <Skeleton variant="text" width="120px" height="20px" sx={{ mb: 1 }} />
                        <Skeleton variant="rectangular" width="100%" height="56px" />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                        <Skeleton variant="text" width="140px" height="20px" sx={{ mb: 1 }} />
                        <Skeleton variant="rectangular" width="100%" height="56px" />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                        <Skeleton variant="text" width="100px" height="20px" sx={{ mb: 1 }} />
                        <Skeleton variant="rectangular" width="100%" height="40px" />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Skeleton variant="rectangular" width="80px" height="36px" sx={{ mr: 1 }} />
                    <Skeleton variant="rectangular" width="70px" height="36px" />
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Subcategory</DialogTitle>
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogContent sx={{ pt: 2 }}>
                    {showSegment && (
                        <div className={styles.field}>
                            <Controller
                                name="segment"
                                control={control}
                                rules={{ required: 'Ledger is required' }}
                                render={({ field, fieldState }) => (
                                    <Autocomplete
                                        options={segments}
                                        value={field.value || initialSegment}
                                        onChange={(event, newValue) => {
                                            handleSegmentChange(newValue, field);
                                        }}
                                        getOptionLabel={(option) => segmentLabel(option)}
                                        isOptionEqualToValue={(option, value) => option === value}
                                        disableClearable
                                        size="small"
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Ledger"
                                                placeholder="Select ledger"
                                                variant="outlined"
                                                fullWidth
                                                size="small"
                                                error={!!fieldState.error}
                                                helperText={fieldState.error?.message}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </div>
                    )}

                    <div className={styles.field}>
                        <Controller
                            name="type"
                            control={control}
                            rules={{ required: 'Type is required' }}
                            render={({ field, fieldState }) => (
                                <Autocomplete
                                    options={getTypes(categoryList, watchedSegment)}
                                    value={field.value || ''}
                                    onChange={(event, newValue) => {
                                        handleCategoryTypeChange(newValue, field);
                                    }}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    size="small"
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Category Type"
                                            placeholder="Select category type"
                                            variant="outlined"
                                            fullWidth
                                            size="small"
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                />
                            )}
                        />
                    </div>

                    <div className={styles.field}>
                        <Controller
                            name="category"
                            control={control}
                            rules={{ required: 'Category is required' }}
                            render={({ field, fieldState }) => (
                                <Autocomplete
                                    freeSolo
                                    options={getCategories(categoryList, watchedSegment, watchedType)}
                                    value={field.value || ''}
                                    onChange={(event, newValue) => {
                                        handleCategoryNameChange(newValue, field);
                                    }}
                                    onInputChange={(event, newInputValue) => {
                                        field.onChange(newInputValue || '');
                                        // Reset subcategory when typing in category
                                        setValue('subcategory', '');
                                    }}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    size="small"
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Category Name"
                                            placeholder="Select or enter category name"
                                            variant="outlined"
                                            fullWidth
                                            size="small"
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                />
                            )}
                        />
                    </div>

                    <div className={styles.field}>
                        <Controller
                            name="subcategory"
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: "Subcategory is required"
                                },
                                validate: {
                                    notEmpty: value => value?.trim() !== '' || "Subcategory cannot be empty",
                                    noDuplicate: value => validateSubcategory(value)
                                }
                            }}
                            render={({ field, fieldState }) => (
                                <Autocomplete
                                    freeSolo
                                    options={getSubcategories(categoryList, watchedSegment, watchedType, watchedCategory)}
                                    value={field.value || ''}
                                    onChange={(event, newValue) => {
                                        field.onChange(newValue || '');
                                    }}
                                    onInputChange={(event, newInputValue) => {
                                        field.onChange(newInputValue || '');
                                    }}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    size="small"
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Subcategory"
                                            placeholder="Select or enter subcategory"
                                            variant="outlined"
                                            fullWidth
                                            size="small"
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                />
                            )}
                        />
                    </div>

                    <div className={styles.rationale}>
                        <Controller
                            name="rationale"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Rationale"
                                    placeholder="Enter any additional rationale..."
                                    variant="outlined"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    size="small"
                                />
                            )}
                        />
                    </div>

                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={!watchedType}>
                        Save
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default CategoryEditDialog;
